/**
 * Data, Schedules — the schedules' technical values, said in words (owner request 2026-10-10,
 * ADR 9057's rule: a name in words, its code behind the «id» button).
 *
 * `GET /foundations/schedules` carries no label or description for a schedule, so the names live
 * here, keyed on the orchestrator's schedule name. Pure, and tested.
 *
 * **Where each name comes from.** Each is the description the schedule declares in bata-data
 * (`dagster/**`, origin/main 2026-10-10), never a guess. A schedule that IS one job a screen already
 * names (the shadow backtest, the elasticity fit, the exception sweep) reuses that job's label
 * (`JOB_LABEL_KEY`); the two schedules that share a job and differ by when they start (the exception
 * sweep's fallback, the stock pull's fallback) say so. A name carries no cadence ("daily",
 * "weekly"): the cron beside it says that, and a deployment can override it with an environment
 * variable. A schedule not listed reads «Pianificazione non ancora descritta» — a wrong name is
 * worse than none, because the planner audits by it.
 *
 * **The cron** is phrased only for the shapes bata-data declares (a daily, weekly or monthly time,
 * a run of days in each month, a minute step); anything else is «personalizzata» and shows the
 * expression itself. **The zone** is the row's own `executionTimezone`; bata-data computes a tick
 * in UTC when a schedule states none (`lib/schedule_state.py` `next_tick`: `execution_timezone or
 * "UTC"`), so a row with none is read as UTC.
 *
 * **A partition key** is Dagster's `<country>|<date>` (`lib/partitions.py`: `CO|2026-09-01`; the
 * monthly axis is keyed on the month's first day, the daily one on the day). Neither the key nor
 * the read says which grain it is, so the key is always said as its date — «CO · 1 settembre 2026».
 */

import type { TFunction } from 'i18next';

import { wordKey } from '@/planning/words';

// ── schedule names ───────────────────────────────────────────────────────────

/** A schedule's name in words. Keys are literals so the catalogue check sees them used. */
export const SCHEDULE_LABEL_KEY: Readonly<Record<string, string>> = {
  exit_cycle_schedule: 'foundations.schedules.name.exitCycle',
  exit_shadow_backtest_schedule: 'words.job.exitShadowBacktest',
  feed_certification_daily: 'foundations.schedules.name.feedCertification',
  foundations_exception_sweep_daily: 'words.job.foundationsExceptionSweep',
  foundations_exception_sweep_fallback: 'foundations.schedules.name.exceptionSweepFallback',
  fx_rate_open_month_schedule: 'foundations.schedules.name.fxRateOpenMonth',
  inseason_door_availability_weekly_schedule: 'foundations.schedules.name.doorAvailability',
  inseason_elasticity_fit_schedule: 'words.job.inseasonElasticityFit',
  inseason_price_weekly_schedule: 'foundations.schedules.name.inseasonPrice',
  inseason_replenishment_daily_fallback_schedule: 'foundations.schedules.name.inseasonReplenishmentFallback',
  nightly_committed_volume_scale_test: 'foundations.schedules.name.scaleTest',
  oracle_cost_weekly_schedule: 'foundations.schedules.name.oracleCost',
  oracle_returns_closing_month_schedule: 'foundations.schedules.name.oracleReturnsClosingMonth',
  oracle_returns_daily_schedule: 'foundations.schedules.name.oracleReturnsLatest',
  oracle_returns_open_month_schedule: 'foundations.schedules.name.oracleReturnsOpenMonth',
  oracle_sales_closing_month_schedule: 'foundations.schedules.name.oracleSalesClosingMonth',
  oracle_sales_daily_schedule: 'foundations.schedules.name.oracleSalesLatest',
  oracle_sales_open_month_schedule: 'foundations.schedules.name.oracleSalesOpenMonth',
  oracle_stock_daily_schedule: 'foundations.schedules.name.oracleStock',
  oracle_stock_fallback_schedule: 'foundations.schedules.name.oracleStockFallback',
  postseason_promotion_article_panel_weekly_schedule: 'foundations.schedules.name.promotionArticlePanel',
  postseason_promotion_weekly_schedule: 'foundations.schedules.name.promotion',
  postseason_weekly_refit_schedule: 'foundations.schedules.name.weeklyRefit',
  programme_benefit_measurement_weekly: 'foundations.schedules.name.programmeBenefit',
  programme_measurement_weekly: 'foundations.schedules.name.programmeMeasurement',
  programme_monthly_report_schedule: 'foundations.schedules.name.programmeMonthlyReport',
  publish_demand_exclusion_windows_schedule: 'foundations.schedules.name.publishDemandExclusion',
  publish_landed_cost_schedule: 'foundations.schedules.name.publishLandedCost',
  publish_pilot_forecast_quantiles_weekly_schedule: 'foundations.schedules.name.publishPilotForecast',
  publish_price_observations_schedule: 'foundations.schedules.name.publishPriceObservations',
  publish_product_attribute_master_schedule: 'foundations.schedules.name.publishProductAttributeMaster',
  publish_retail_calendar_schedule: 'foundations.schedules.name.publishRetailCalendar',
  publish_retail_price_schedule: 'foundations.schedules.name.publishRetailPrice',
  publish_returns_transactions_schedule: 'foundations.schedules.name.publishReturns',
  publish_sales_transactions_schedule: 'foundations.schedules.name.publishSales',
  publish_size_dimension_schedule: 'foundations.schedules.name.publishSizeDimension',
  reap_orphaned_dbt_tables_daily: 'foundations.schedules.name.reapOrphanedTables',
};

/** The catalogue key of a schedule's name; `null` when this build has no words for it. */
export function scheduleNameKey(name: string): string | null {
  return wordKey(SCHEDULE_LABEL_KEY, name);
}

/** The schedule in words, or «Pianificazione non ancora descritta». Pure. */
export function scheduleTitle(t: TFunction, name: string): string {
  const key = scheduleNameKey(name);
  return key === null ? t('foundations.schedules.name.unknown') : t(key);
}

// ── cron ─────────────────────────────────────────────────────────────────────

export type CronWords =
  | { readonly kind: 'words'; readonly key: string; readonly values: Readonly<Record<string, string | number>>; readonly weekday?: number }
  | { readonly kind: 'custom' };

const CUSTOM: CronWords = { kind: 'custom' };
const NUMBER = /^\d{1,2}$/;
const STEP = /^\*\/(\d{1,2})$/;
const RANGE = /^(\d{1,2})-(\d{1,2})$/;

const within = (text: string, lowest: number, highest: number): number | null => {
  if (!NUMBER.test(text)) return null;
  const value = Number(text);
  return value >= lowest && value <= highest ? value : null;
};

const two = (value: number): string => String(value).padStart(2, '0');

/** The zone a row's schedule ticks in: its own, else UTC (bata-data's `next_tick` default). */
export function scheduleZone(executionTimezone: string | null | undefined): string {
  return executionTimezone === null || executionTimezone === undefined || executionTimezone.trim() === '' ? 'UTC' : executionTimezone.trim();
}

/**
 * A five-field cron in words, for the shapes the estate declares:
 *
 * | cron            | phrase                                   |
 * | `* * * * *`     | every minute                             |
 * | a step in minutes, 15 for example | every 15 minutes      |
 * | `20 * * * *`    | every hour, at minute 20                 |
 * | `0 6 * * *`     | every day at 06:00                       |
 * | `0 6 * * 1`     | every Monday at 06:00 (0 and 7 = Sunday) |
 * | `0 9 1 * *`     | on day 1 of each month at 09:00          |
 * | `15 10 1-12 * *`| on days 1 to 12 of each month at 10:15   |
 *
 * Anything else — a list, a month or weekday field, names, a step on the hour, an out-of-range
 * number, several expressions — is `custom`: the caller shows the expression itself. Pure.
 */
export function cronWords(cron: string | null | undefined, executionTimezone?: string | null): CronWords {
  if (cron === null || cron === undefined) return CUSTOM;
  const fields = cron.trim().split(/\s+/);
  if (fields.length !== 5) return CUSTOM;
  const [minuteField, hourField, dayField, monthField, weekdayField] = fields as [string, string, string, string, string];
  if (monthField !== '*') return CUSTOM;
  const zone = scheduleZone(executionTimezone);

  if (hourField === '*' && dayField === '*' && weekdayField === '*') {
    if (minuteField === '*') return { kind: 'words', key: 'foundations.schedules.cron.everyMinute', values: {} };
    const step = STEP.exec(minuteField);
    if (step !== null) {
      const n = Number(step[1]);
      if (n < 1 || n > 59) return CUSTOM;
      return n === 1
        ? { kind: 'words', key: 'foundations.schedules.cron.everyMinute', values: {} }
        : { kind: 'words', key: 'foundations.schedules.cron.everyMinutes', values: { count: n } };
    }
    const minute = within(minuteField, 0, 59);
    return minute === null ? CUSTOM : { kind: 'words', key: 'foundations.schedules.cron.hourly', values: { minute } };
  }

  const minute = within(minuteField, 0, 59);
  const hour = within(hourField, 0, 23);
  if (minute === null || hour === null) return CUSTOM;
  const values = { time: `${two(hour)}:${two(minute)}`, zone };

  if (dayField === '*' && weekdayField === '*') return { kind: 'words', key: 'foundations.schedules.cron.daily', values };
  if (dayField === '*') {
    const weekday = within(weekdayField, 0, 7);
    return weekday === null ? CUSTOM : { kind: 'words', key: 'foundations.schedules.cron.weekly', values, weekday: weekday % 7 };
  }
  if (weekdayField !== '*') return CUSTOM;
  const day = within(dayField, 1, 31);
  if (day !== null) return { kind: 'words', key: 'foundations.schedules.cron.monthly', values: { ...values, day } };
  const range = RANGE.exec(dayField);
  if (range === null) return CUSTOM;
  const from = within(range[1] ?? '', 1, 31);
  const to = within(range[2] ?? '', 1, 31);
  if (from === null || to === null || from >= to) return CUSTOM;
  return { kind: 'words', key: 'foundations.schedules.cron.monthDays', values: { ...values, from, to } };
}

/** A weekday (0 = Sunday) in the locale's own word, UTC so no zone shifts it. */
export function weekdayName(weekday: number, language: string): string {
  // 2024-01-07 was a Sunday.
  return new Intl.DateTimeFormat(language, { weekday: 'long', timeZone: 'UTC' }).format(new Date(Date.UTC(2024, 0, 7 + weekday)));
}

/** The cron said in words, or `null` when it is custom (the caller shows the expression). Pure. */
export function cronText(t: TFunction, language: string, cron: string | null | undefined, executionTimezone?: string | null): string | null {
  const words = cronWords(cron, executionTimezone);
  if (words.kind === 'custom') return null;
  const values = words.weekday === undefined ? words.values : { ...words.values, weekday: weekdayName(words.weekday, language) };
  return t(words.key, values);
}

// ── partition keys ───────────────────────────────────────────────────────────

export interface PartitionWords {
  readonly country: string | null;
  /** The calendar date the key carries, `YYYY-MM-DD`. */
  readonly iso: string;
}

const PARTITION = /^(?:([A-Z]{2})\|)?(\d{4})-(\d{2})-(\d{2})$/;

/**
 * What a run's `partitionKey` carries: a market and a date, or `null` for any other shape. The read
 * does not say whether the partition is a month or a day (a month's key is its first day), so the
 * key is always read as the date it is — never as a month, which would be false for a daily one.
 */
export function partitionWords(key: string | null | undefined): PartitionWords | null {
  if (key === null || key === undefined) return null;
  const match = PARTITION.exec(key.trim());
  if (match === null) return null;
  const [, country, year, month, day] = match;
  const iso = `${year}-${month}-${day}`;
  const at = new Date(`${iso}T00:00:00Z`);
  if (Number.isNaN(at.getTime()) || at.toISOString().slice(0, 10) !== iso) return null;
  return { country: country ?? null, iso };
}

/** The partition in words — «CO · 1 ottobre 2026» — or `null` when its shape is unknown. Pure. */
export function partitionText(key: string | null | undefined, language: string): string | null {
  const words = partitionWords(key);
  if (words === null) return null;
  const date = new Intl.DateTimeFormat(language, { dateStyle: 'long', timeZone: 'UTC' }).format(new Date(`${words.iso}T00:00:00Z`));
  return words.country === null ? date : `${words.country} · ${date}`;
}
